/**
 * SectionBase: which door a shared page was opened through (SASE plan SD-01,
 * §7.1 rules 2–3, SASE-18).
 *
 * A service shared by the cloud and the SASE section is one feature module
 * mounted at two paths, never a copy: Private access at `/access` and
 * `/sase/private-access`, the agent gateway at `/agent-gateways` and
 * `/sase/ai/agent-gateways`. The module builds every internal link and
 * breadcrumb from this context, so a page opened from SASE keeps the visitor
 * under `/sase/…`. With no provider the context is the cloud door, which is
 * exactly the paths the modules used before (behaviour outside SASE is
 * unchanged).
 */
import { createContext, createElement, useContext, type ReactNode } from "react";
import { Link } from "react-router-dom";

/** One step of a breadcrumb trail; `to` makes it a link. */
export interface Crumb {
  title: string;
  to?: string;
}

export type Door = "cloud" | "sase";

export interface SectionBase {
  door: Door;
  /** The section's path prefix: "" for the cloud, "/sase" for SASE. */
  prefix: string;
  /** Where Private access is mounted. */
  privateAccess: string;
  /** Where the identity pages (users, groups, sign-in, provisioning, devices)
   *  live. `null`: they are tabs of the Private access page (the cloud door).
   *  In the SASE door they are SASE → Users and devices (plan §7.3). */
  identity: string | null;
  /** Where the agent gateway module is mounted. */
  agentGateways: string;
  /** The trail above Private access's own crumbs. */
  privateAccessTrail: Crumb[];
  /** The trail above Agent gateways' own crumbs. */
  agentGatewaysTrail: Crumb[];
}

export const SASE_HOME = "/sase";
export const SASE_TITLE = "SASE";
export const AI_SECURITY_TITLE = "AI security";
export const USERS_AND_DEVICES_TITLE = "Users and devices";

/** Today's paths: Access → Private access, Compute → Agent gateways. */
export const CLOUD_BASE: SectionBase = {
  door: "cloud",
  prefix: "",
  privateAccess: "/access",
  identity: null,
  agentGateways: "/agent-gateways",
  privateAccessTrail: [],
  agentGatewaysTrail: [],
};

/** The SASE section's doors onto the same modules (plan §7.1 table). */
export const SASE_BASE: SectionBase = {
  door: "sase",
  prefix: SASE_HOME,
  privateAccess: `${SASE_HOME}/private-access`,
  identity: `${SASE_HOME}/users`,
  agentGateways: `${SASE_HOME}/ai/agent-gateways`,
  privateAccessTrail: [{ title: SASE_TITLE, to: SASE_HOME }],
  agentGatewaysTrail: [{ title: SASE_TITLE, to: SASE_HOME }, { title: AI_SECURITY_TITLE }],
};

/** Mount a door with `<SectionBaseContext.Provider value={SASE_BASE}>`. */
export const SectionBaseContext = createContext<SectionBase>(CLOUD_BASE);

/** The door the current page was opened through (the cloud's by default). */
export function useSectionBase(): SectionBase {
  return useContext(SectionBaseContext);
}

/** Private access's tabs that are identity pages. In the SASE door they are
 *  not tabs of Private access but SASE → Users and devices, so the section
 *  never shows the same page twice (plan §7.3 Private access). */
export const IDENTITY_TABS = ["users", "groups", "sign-in", "provisioning", "devices"] as const;
export type IdentityTab = (typeof IDENTITY_TABS)[number];

export function isIdentityTab(tab: string | null | undefined): tab is IdentityTab {
  return (IDENTITY_TABS as readonly string[]).includes(tab ?? "");
}

/** A tab of Private access in this door: `/access?tab=apps`, or, for an
 *  identity tab in the SASE door, `/sase/users?tab=groups`. */
export function accessTabPath(base: SectionBase, tab: string): string {
  if (base.identity && isIdentityTab(tab)) return `${base.identity}?tab=${tab}`;
  return tab === "overview" ? base.privateAccess : `${base.privateAccess}?tab=${tab}`;
}

/** An agent gateway's page in this door (the list without an id). */
export function gatewayPath(base: SectionBase, id?: string): string {
  return id ? `${base.agentGateways}/${id}` : base.agentGateways;
}

/** A trail as breadcrumb items (PageContainer / antd Breadcrumb), each step
 *  with a `to` a router link, then the page's own crumbs. */
export function trailItems(trail: Crumb[], ...own: ReactNode[]): { title: ReactNode }[] {
  return [
    ...trail.map((c) => ({ title: c.to ? createElement(Link, { to: c.to }, c.title) : c.title })),
    ...own.map((title) => ({ title })),
  ];
}
